/**
 * Formatting utility functions for prospect profile editing
 */

/**
 * Parse height string to feet and inches
 * @param height Height string like "6'1""
 * @returns Object with feet and inches as strings
 */
export const parseHeight = (height: string | null): { feet: string; inches: string } => {
  if (!height) return { feet: '', inches: '' };
  const match = height.match(/(\d+)'(\d+)"/);
  return match ? { feet: match[1], inches: match[2] } : { feet: '', inches: '' };
};

/**
 * Format feet and inches into height string
 * @param feet Feet as string
 * @param inches Inches as string
 * @returns Formatted height like "6'1"" or undefined if incomplete
 */
export const formatHeight = (feet: string, inches: string): string | undefined => {
  if (!feet || !inches) return undefined;
  return `${feet}'${inches}"`;
};

/**
 * Format phone number as (XXX) XXX-XXXX
 * @param text Phone number input
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (text: string): string => {
  if (!text) return '';
  const digits = text.replace(/\D/g, '');

  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

/**
 * Strip phone number formatting for API
 * @param phone Formatted phone number
 * @returns Digits only
 */
export const stripPhoneFormat = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Build a `tel:` href for click-to-dial, keeping only digits and a leading `+`.
 *
 * Stored phone numbers are free text (manual entry, CSV import, AI roster extraction), so a
 * raw value can carry pause/DTMF suffixes, extensions, or a `#` that would truncate the URI.
 * Sanitizing here keeps the number that is dialed identical to the one displayed by
 * `formatPhoneNumber`. Returns null when there are no digits to dial.
 */
export const toTelHref = (phone: string | null | undefined): string | null => {
  if (!phone) return null;
  const leadingPlus = phone.trimStart().startsWith('+') ? '+' : '';
  const digits = phone.replace(/\D/g, '');
  return digits ? `tel:${leadingPlus}${digits}` : null;
};

/**
 * Strip "@" characters from a handle/username value.
 * Handles are stored without the "@" prefix; the prefix is purely visual.
 * @param value Handle input (may contain "@")
 * @returns Handle without any "@" characters
 */
export const stripHandlePrefix = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/@/g, '');
};

/**
 * Format date input as MM/DD/YYYY
 * @param text Date input
 * @returns Formatted date string
 */
export const formatDateInput = (text: string): string => {
  if (!text) return '';
  const digits = text.replace(/\D/g, '');

  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
};

/**
 * Format ISO date string for display as MM/DD/YYYY
 * @param dateString ISO date string or MM/DD/YYYY
 * @returns MM/DD/YYYY formatted date
 */
export const formatDateForDisplay = (dateString: string | null): string => {
  if (!dateString) return '';

  // If already in MM/DD/YYYY format, return as-is
  if (dateString.includes('/')) return dateString;

  // Convert ISO to MM/DD/YYYY
  try {
    const date = new Date(dateString);

    // Validate the date is valid (not Invalid Date)
    if (isNaN(date.getTime())) {
      return '';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return '';
  }
};
